import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect
} from "react";
import {
  mapGeoJSONPolygonToGoogleMapPolygon,
  mapGoogleMapPolygonToGeoJSONPolygon,
  polygonVertexCauseSelfIntersection
} from "../utils";
import { Polygon, Marker } from "@react-google-maps/api";
import { useDispatch } from "react-redux";
import { setErrorToastMessage } from "../../../../redux/geofence/actions";

/**
 * Note: we need vertex to move when the marker is being dragged.
 * So we are using something called MVCObject and bindTo
 * See official docs for more details what this object and method
 * do. Also my experiment fiddle: https://jsfiddle.net/8jbez2dL/
 */

function Geofence({
  editable,
  fence,
  onUpdate,
  onSelectHandler,
  selectedVertex,
  onVertexSelect
}) {
  const [fenceGeoJson, setFenceGeoJson] = useState(fence);
  useEffect(() => {
    setFenceGeoJson(fence);
  }, [fence]);

  const [editableState, setEditableState] = useState(editable);
  useEffect(() => {
    setEditableState(editable);
  }, [editable]);

  const polygon = useMemo(
    () => mapGeoJSONPolygonToGoogleMapPolygon(fenceGeoJson, editableState),
    [fenceGeoJson, editableState]
  );
  const { paths, options } = polygon;
  const polygonRef = useRef(null);
  const listenersRef = useRef([]);
  const markerRefs = useRef([]);
  const dispatch = useDispatch();

  // TODO: Figure out right place
  //const cleanUp = () => {};

  const onSelect = _id => {
    if (onSelectHandler) {
      onSelectHandler(_id);
    }
  };

  const onMove = useCallback(
    index => {
      const updatedFence = mapGoogleMapPolygonToGeoJSONPolygon(
        polygonRef.current,
        {
          ...fenceGeoJson.properties
        },
        fenceGeoJson._id
      );
      if (polygonVertexCauseSelfIntersection(updatedFence, index)) {
        dispatch(
          setErrorToastMessage("Polygon self intersecting not allowed.")
        );
        setFenceGeoJson({ ...fenceGeoJson });

        // Hack don't remove the below lines ¯\_(ツ)_/¯
        // causes react to redraw marker and maybe the polygon too
        // remove the line traces left after the geofence is
        // stopped from updating. To understand the issue try removing
        // the lines and update the polygion and make it self
        // intersecting.
        setEditableState(false);
        setEditableState(true);
        // hack end
        return;
      }
      setFenceGeoJson(updatedFence);
      if (onUpdate) {
        onUpdate(updatedFence);
      }
    },
    [onUpdate, fenceGeoJson, dispatch]
  );

  const onAdd = useCallback(
    index => {
      const updatedFence = mapGoogleMapPolygonToGeoJSONPolygon(
        polygonRef.current,
        {
          ...fenceGeoJson.properties
        },
        fenceGeoJson._id
      );
      if (polygonVertexCauseSelfIntersection(updatedFence, index)) {
        dispatch(
          setErrorToastMessage("Polygon self intersecting not allowed.")
        );
        setFenceGeoJson({ ...fenceGeoJson });
        return;
      }
      setFenceGeoJson(updatedFence);
      if (onVertexSelect && index <= selectedVertex) {
        onVertexSelect(selectedVertex + 1);
      }
      if (onUpdate) {
        onUpdate(updatedFence);
      }
    },
    [onUpdate, onVertexSelect, selectedVertex, fenceGeoJson, dispatch]
  );

  // Reregister event because for some reason these events get deregister
  useEffect(() => {
    if (polygonRef.current) {
      const path = polygonRef.current.getPath();
      listenersRef.current.push(
        path.addListener("set_at", onMove),
        path.addListener("insert_at", onAdd),
        path.addListener("remove_at", onAdd)
      );
    }
  }, [fenceGeoJson, onAdd, onMove, polygon]);

  const onLoad = useCallback(
    polygon => {
      polygonRef.current = polygon;
      const path = polygon.getPath();
      listenersRef.current.push(
        path.addListener("set_at", onMove),
        path.addListener("insert_at", onAdd),
        path.addListener("remove_at", onAdd)
      );
    },
    [onAdd, onMove]
  );
  /*
  useEffect(() => {
    if (polygonInstance) {
    }
  }, []);*/

  const resetVertexClickVertices = () => {
    markerRefs.current.forEach(e => {
      // if selected
      if (e.icon.strokeOpacity === 0.4) {
        e.setIcon({ ...e.icon, strokeOpacity: 1, strokeWeight: 2 });
      }
    });
  };
  const onVertexClick = index => {
    // can only click one vertex at a time
    resetVertexClickVertices();
    const selectedMarker = markerRefs.current[index];
    selectedMarker.setIcon({
      ...selectedMarker.icon,
      strokeWeight: 18,
      strokeOpacity: 0.4
    });
    if (onVertexSelect) onVertexSelect(index);
  };

  const onMarkerLoad = (index, markerInstance) => {
    markerRefs.current[index] = markerInstance;
    markerInstance.addListener("position_changed", () => {
      polygonRef.current.getPath().setAt(index, markerInstance.getPosition());
    });
  };
  /*
  useEffect(() => {
    markerInstances.forEach((e, index) =>
      e.bindTo("position", binder.current, index.toString())
    );
  }, [markerInstances]);
  */
  const onUnmount = useCallback(() => {
    listenersRef.current.forEach(lis => lis.remove());
    polygonRef.current = null;
  }, []);
  return (
    <div className="Geofence">
      <Polygon
        onClick={() => {
          onSelect(fenceGeoJson._id);
        }}
        onLoad={onLoad}
        paths={paths}
        options={{ ...options }}
        onUnmount={onUnmount}
      />
      {editableState
        ? paths[0].map((position, index) => {
            return (
              <Marker
                key={index}
                label={{
                  text: index.toString(),
                  color: "#ffffff",
                  fontWeight: "bold"
                }}
                cursor={"default"}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  origin: new window.google.maps.Point(0, 0),
                  anchor: new window.google.maps.Point(0, 1.5),
                  labelOrigin: new window.google.maps.Point(0, 0),
                  scale: 10,
                  strokeWeight: index === selectedVertex ? 18 : 2,
                  strokeOpacity: index === selectedVertex ? 0.4 : 1,
                  fillOpacity: 1,
                  strokeColor: "rgba(50, 50, 53)",
                  fillColor: "#323235"
                }}
                onClick={() => {
                  onVertexClick(index);
                }}
                onLoad={loadedMarker => onMarkerLoad(index, loadedMarker)}
                position={position}
              />
            );
          })
        : null}
    </div>
  );
}

Geofence.defaultProps = {};

Geofence.propTypes = {};

export default Geofence;
